// MOVIMENTO — o arquivo que a Atlas entrega à prefeitura, e o único formato de
// saída.
//
//   ADF        autoriza o desconto do contrato em folha
//   MOVIMENTO  informa mensalmente à prefeitura quais parcelas processar
//   RETORNO    informa à Atlas quais parcelas foram efetivamente descontadas
//
// O layout está em `remessa-perfil.ts` (`COLUNAS_MOVIMENTO`), medido em 150
// linhas de três arquivos reais. Este módulo é só a serialização — o modelo
// (`RemessaEntrada`) continua neutro.
//
// A DISCIPLINA AQUI É DIFERENTE DA DO DELIMITADO. Em largura fixa, uma célula
// fora da medida DESLOCA TODAS AS SEGUINTES, e o arquivo continua parecendo bem
// formado: a prefeitura importa, o desconto sai errado, e ninguém vê erro de
// parse. Então este serializador falha alto — exceto onde truncar é a regra
// declarada (o nome, 50 posições).
//
// `montarLinhaDelimitada` não serve por três motivos: ignora `largura`/`preenche`,
// formata dinheiro com ponto (`233.17` em vez de `000000000233,17`), e põe aspas
// quando a célula contém o separador — aspas quebram largura fixa.
import { validarLayoutColunas } from "./csv-delimitado.js";
import { COLUNAS_MOVIMENTO, MOVIMENTO_SEPARADOR } from "./movimento-layout.js";
/**
 * Tipo de movimento da linha.
 *
 * Padrão nosso — o arquivo real do ConsigFácil só trouxe `I` nas 150 linhas
 * medidas, e o cliente confirmou (27/08/2026) que o campo aceita os três.
 *
 *   `I`  a dívida é instruída nesta competência. É o caso normal, e vale para a
 *        posição INTEIRA, não só para o que começou agora — o movimento é um
 *        retrato do mês, não um delta.
 *   `A`  valor ou cronograma mudaram desde a competência anterior.
 *   `E`  PARE de descontar: quitada, desligamento ou suspensão.
 *
 * O `E` é cinto e suspensório. Quem lê a posição inteira já para pela ausência
 * da linha no mês seguinte; quem só ADICIONA o que vem no arquivo continuaria
 * descontando dívida encerrada para sempre. Emitir o `E` por uma competência
 * cobre os dois comportamentos de importador.
 */
export const TIPOS_MOVIMENTO = ["I", "A", "E"];
/** Sobe quando coluna entra/sai ou regra de montagem muda. */
export const MOVIMENTO_VERSAO = "1.0";
/** Dado da remessa que o movimento não consegue escrever — verba longa, CPF
 *  mascarado, valor que estoura a largura. Exportada para a API recusar com a
 *  mensagem, em vez de devolver "Erro interno" e esconder o que corrigir. */
export class MovimentoInvalido extends Error {
    constructor(mensagem) {
        super(mensagem);
        this.name = "MovimentoInvalido";
    }
}
/** Só dígitos, e só se forem 11. Máscara é RECUSADA, não limpa: `123.***.***-45`
 *  sobreviveria a um `replace(/\D/g)` como `12345` — um CPF plausível e errado,
 *  pior que campo vazio. */
function cpfMascarado(cpf, matricula) {
    const bruto = (cpf ?? "").trim();
    if (bruto === "")
        return "";
    if (bruto.includes("*")) {
        throw new MovimentoInvalido(`cpf mascarado na matricula ${matricula}: o movimento leva o CPF em claro, e a folha localiza o servidor por ele`);
    }
    const d = bruto.replace(/\D/g, "");
    if (d.length !== 11) {
        throw new MovimentoInvalido(`cpf com ${d.length} digitos na matricula ${matricula}: esperado 11 ou vazio`);
    }
    return `${d.slice(0, 3)}.${d.slice(3, 6)}.${d.slice(6, 9)}-${d.slice(9)}`;
}
/** `233.17` -> `000000000233,17`. Doze posições de inteiro, vírgula, dois centavos. */
function dinheiroMovimento(reais, matricula) {
    const centavos = Math.round(reais * 100);
    if (!Number.isFinite(centavos) || centavos < 0) {
        throw new MovimentoInvalido(`valor invalido na matricula ${matricula}: ${reais}`);
    }
    const inteiro = String(Math.trunc(centavos / 100));
    if (inteiro.length > 12) {
        throw new MovimentoInvalido(`valor ${reais} na matricula ${matricula} nao cabe em 12 digitos de inteiro`);
    }
    return `${inteiro.padStart(12, "0")},${String(centavos % 100).padStart(2, "0")}`;
}
/** Número em N posições, zeros à esquerda. Estourar é ERRO — truncar um número
 *  pela esquerda mantém os dígitos menos significativos e corrompe em silêncio. */
function numeroEm(n, largura, oQue, matricula) {
    const s = String(Math.trunc(n));
    if (s.length > largura) {
        throw new MovimentoInvalido(`${oQue} ${n} na matricula ${matricula} nao cabe em ${largura} posicoes`);
    }
    return s.padStart(largura, "0");
}
/** Texto em N posições, espaços à direita. `truncar` só onde a regra permite. */
function textoEm(v, largura, oQue, matricula, truncar) {
    // Separador dentro da célula é REMOVIDO, não escapado: aspas quebrariam a
    // largura fixa e deslocariam o resto da linha.
    const limpo = v.replace(new RegExp(`\\${MOVIMENTO_SEPARADOR}`, "g"), " ").replace(/[\r\n]/g, " ");
    if (limpo.length > largura) {
        if (!truncar) {
            throw new MovimentoInvalido(`${oQue} "${v}" na matricula ${matricula} nao cabe em ${largura} posicoes`);
        }
        return limpo.slice(0, largura);
    }
    return limpo.padEnd(largura, " ");
}
/** Parcelas que ainda faltam, INCLUINDO a do mês. Confirmado em 150/150 linhas
 *  reais e em 301/301 na série do ADR-0003. Ler como `total - atual` erra por um. */
export function restantesDoMovimento(d) {
    if (d.semTermino || !(d.parcelasTotal > 0))
        return 0;
    return d.parcelasTotal - d.parcelaNumero + 1;
}
function larguraDe(nome) {
    const c = COLUNAS_MOVIMENTO.find((x) => x.nome === nome);
    if (!c?.largura)
        throw new Error(`coluna "${nome}" sem largura no layout do movimento`);
    return c.largura;
}
function linhaDoMovimento(d, competencia) {
    const m = d.matricula;
    const celulas = [
        // Único campo de largura variável: no arquivo real aparece com 5 e com 7
        // dígitos. Não é preenchido nem truncado.
        m,
        textoEm(d.nome ?? "", larguraDe("nome"), "nome", m, true),
        textoEm(cpfMascarado(d.cpf, m), larguraDe("cpf"), "cpf", m, false),
        textoEm(d.tipoMovimento ?? "I", larguraDe("tipoMovimento"), "tipo de movimento", m, false),
        numeroEm(d.parcelaNumero, larguraDe("parcelaNumero"), "parcela", m),
        numeroEm(d.parcelasTotal, larguraDe("parcelasTotal"), "total de parcelas", m),
        numeroEm(restantesDoMovimento(d), larguraDe("parcelasRestantes"), "parcelas restantes", m),
        dinheiroMovimento(d.valorParcela, m),
        // A ADF, no campo que o ConsigFácil manda vazio. É o que permite ao retorno
        // casar a dívida sem ambiguidade quando o servidor tem duas na mesma verba.
        textoEm(d.adf, larguraDe("referencia"), "referencia (ADF)", m, false),
        textoEm(d.codigoVerba ?? "", larguraDe("codigoVerba"), "verba", m, false),
        competencia,
    ];
    return celulas.join(MOVIMENTO_SEPARADOR);
}
/**
 * O arquivo do mês, uma linha por dívida a descontar.
 *
 * CRLF entre as linhas e nenhuma linha em branco no fim quando não há desconto —
 * mesma disciplina do layout antigo, e é o que o ERP municipal produz.
 */
export function serializarMovimento(entrada) {
    const problemas = validarLayoutColunas(COLUNAS_MOVIMENTO);
    if (problemas.length > 0)
        throw new Error(`layout do movimento inconsistente: ${problemas.join("; ")}`);
    const linhas = entrada.descontos.map((d) => linhaDoMovimento(d, entrada.competencia));
    return linhas.length === 0 ? "" : `${linhas.join("\r\n")}\r\n`;
}
/**
 * `movimento_<prefeitura>_<competencia>.csv`.
 *
 * O sufixo `-previa` existe porque o movimento é gerado sob demanda e só congela
 * na data de corte: antes disso ele ainda muda. Entregar prévia e definitivo com
 * o mesmo nome deixaria a prefeitura processar um pelo outro.
 *
 * Os dois exemplos reais divergem entre si — um tem espaço antes do `_` da
 * competência e o outro não. Não há regra a copiar: emitimos sem o espaço.
 */
export function nomeArquivoMovimento(entrada) {
    const nome = entrada.prefeituraNome.replace(/[\\/:*?"<>|]/g, "").trim();
    const previa = entrada.congelada === false ? "-previa" : "";
    return `movimento_${nome}_${entrada.competencia}${previa}.csv`;
}
//# sourceMappingURL=movimento.js.map